import { Link } from "react-router-dom";
import { useState } from "react";
import {
  Bell,
  BriefcaseBusiness,
  CheckCircle2,
  ChevronRight,
  Globe2,
  LockKeyhole,
  Mail,
  MapPin,
  MessageCircle,
  Moon,
  Phone,
  Plug,
  ShieldCheck,
  Sun,
  UserCircle,
} from "lucide-react";
import AdminSidebar from "../components/AdminSidebar";
import useAdminTheme from "../hooks/useAdminTheme";

function SettingsPage() {
  const adminEmail = process.env.REACT_APP_ADMIN_EMAIL || "Admin email not set";
  const { isDark, toggleTheme } = useAdminTheme();
  const [activeTab, setActiveTab] = useState("business");

  const menuItems = [
    { id: "profile", label: "Profile", icon: UserCircle },
    { id: "security", label: "Security", icon: ShieldCheck },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "business", label: "Business", icon: BriefcaseBusiness },
    { id: "integrations", label: "Integrations", icon: Plug },
  ];

  const businessFields = [
    {
      label: "Business name",
      value: "Limassol Airport Taxi",
      icon: BriefcaseBusiness,
    },
    {
      label: "Phone number",
      value: "+357 96613191",
      icon: Phone,
    },
    {
      label: "Email",
      value: "info@limassolairporttaxi.com",
      icon: Mail,
    },
    {
      label: "Website",
      value: "limassolairporttaxi.com",
      icon: Globe2,
    },
    {
      label: "Location",
      value: "Limassol, Cyprus",
      icon: MapPin,
    },
  ];

  const securityItems = [
    {
      label: "Admin email",
      value: adminEmail,
      helper: "Used for administrator access and reset flows.",
    },
    {
      label: "Session timeout",
      value: "Enabled",
      helper: "Inactive admin sessions are signed out automatically.",
    },
    {
      label: "Supabase RLS",
      value: "Enabled",
      helper: "Booking reads and updates are protected by row level security.",
    },
  ];

  const notificationItems = [
    {
      label: "Booking email notifications",
      value: "Configured",
      helper: "Booking emails are sent by the Supabase Edge Function.",
      icon: Mail,
    },
    {
      label: "WhatsApp booking status",
      value: "Active",
      helper: "WhatsApp CTAs remain available across booking flows.",
      icon: MessageCircle,
    },
    {
      label: "GA4 tracking status",
      value: "Consent based",
      helper: "Analytics events run after visitor cookie consent.",
      icon: Globe2,
    },
  ];

  const integrations = [
    {
      name: "Supabase",
      status: "Connected",
      detail: "Admin auth and booking data",
    },
    {
      name: "Google Analytics",
      status: "Configured",
      detail: "GA4 event tracking",
    },
    {
      name: "Google Maps",
      status: "Configured",
      detail: "Location autocomplete",
    },
    {
      name: "Resend",
      status: "Configured",
      detail: "Booking email delivery via Supabase Edge Function",
    },
    {
      name: "Hostinger Deployment",
      status: "Ready",
      detail: "Deployment workflow managed separately",
    },
  ];

  const ui = {
    page: isDark ? "bg-[#080808] text-white" : "bg-slate-50 text-slate-950",
    card: isDark
      ? "border-white/10 bg-neutral-950 shadow-2xl shadow-black/30"
      : "border-slate-200 bg-white shadow-sm",
    softCard: isDark
      ? "border-white/10 bg-black/30"
      : "border-slate-100 bg-slate-50",
    strongText: isDark ? "text-white" : "text-slate-950",
    bodyText: isDark ? "text-neutral-300" : "text-slate-700",
    mutedText: isDark ? "text-neutral-400" : "text-slate-500",
    faintText: isDark ? "text-neutral-500" : "text-slate-400",
    input: isDark
      ? "border-white/10 bg-black/40 text-white"
      : "border-slate-200 bg-slate-50 text-slate-800 focus:bg-white",
    divider: isDark ? "border-white/10" : "border-slate-100",
    yellowBadge: isDark
      ? "border border-yellow-400/20 bg-yellow-400/10 text-yellow-300"
      : "bg-yellow-100 text-yellow-800",
  };

  const SettingsCard = ({ children, className = "" }) => (
    <div className={`rounded-2xl border p-4 ${ui.softCard} ${className}`}>
      {children}
    </div>
  );

  return (
    <div className={`min-h-screen ${ui.page}`}>
      <AdminSidebar />

      <main
        className="min-h-screen px-4 py-5 transition-all duration-300 sm:px-6 lg:px-8"
        style={{ marginLeft: "var(--admin-sidebar-width, 16rem)" }}
      >
        <div className="mx-auto max-w-[1400px]">
          <header className={`mb-6 rounded-2xl border p-5 sm:p-6 ${ui.card}`}>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className={`mb-3 flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] ${ui.yellowBadge}`}>
                  <ShieldCheck size={14} />
                  Admin control
                </div>
                <h1 className={`text-3xl font-black tracking-tight sm:text-4xl ${ui.strongText}`}>
                  Settings
                </h1>
                <p className={`mt-2 max-w-2xl text-sm ${ui.mutedText}`}>
                  Manage business details, security preferences, notifications,
                  and platform integrations.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className={`rounded-2xl border px-4 py-3 ${isDark ? "border-yellow-400/20 bg-yellow-400/10" : "border-yellow-200 bg-yellow-50"}`}>
                  <p className={`text-xs font-bold uppercase tracking-wide ${isDark ? "text-yellow-300" : "text-yellow-800"}`}>
                    Business profile
                  </p>
                  <p className={`mt-1 text-sm font-black ${ui.strongText}`}>
                    Limassol Airport Taxi
                  </p>
                </div>
                <button
                  type="button"
                  onClick={toggleTheme}
                  className={`inline-flex items-center justify-center gap-2 rounded-2xl border px-5 py-3 text-sm font-black transition ${
                    isDark
                      ? "border-white/10 bg-white/5 text-white hover:bg-white/10"
                      : "border-slate-200 bg-white text-slate-800 hover:border-yellow-300 hover:bg-yellow-50"
                  }`}
                >
                  {isDark ? <Sun size={17} /> : <Moon size={17} />}
                  {isDark ? "Light" : "Dark"}
                </button>
              </div>
            </div>
          </header>

          <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
            <aside className={`rounded-2xl border p-3 lg:sticky lg:top-5 lg:self-start ${ui.card}`}>
              <div className={`border-b px-3 py-4 ${ui.divider}`}>
                <p className={`text-sm font-black ${ui.strongText}`}>
                  Settings menu
                </p>
                <p className={`mt-1 text-xs ${ui.mutedText}`}>
                  Configure admin workspace
                </p>
              </div>

              <nav className="mt-3 space-y-1">
                {menuItems.map(({ id, label, icon: Icon }) => {
                  const active = activeTab === id;

                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setActiveTab(id)}
                      className={`flex w-full items-center justify-between rounded-xl px-3 py-3 text-left text-sm font-bold transition ${
                        active
                          ? isDark
                            ? "bg-yellow-400/10 text-yellow-300"
                            : "bg-yellow-100 text-yellow-900"
                          : isDark
                            ? "text-neutral-300 hover:bg-white/10 hover:text-white"
                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <Icon size={18} />
                        {label}
                      </span>
                      <ChevronRight
                        size={16}
                        className={active ? "text-yellow-700" : ui.faintText}
                      />
                    </button>
                  );
                })}
              </nav>
            </aside>

            <div className="space-y-6">
              {activeTab === "profile" && (
                <section className={`rounded-2xl border p-5 sm:p-6 ${ui.card}`}>
                  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h2 className={`text-xl font-black ${ui.strongText}`}>
                        Business Profile
                      </h2>
                      <p className={`mt-1 text-sm ${ui.mutedText}`}>
                        Public identity and contact details for the business.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="inline-flex w-fit items-center justify-center rounded-2xl bg-yellow-400 px-5 py-3 text-sm font-black text-slate-950 transition hover:bg-yellow-300"
                    >
                      Edit Profile
                    </button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {[
                      {
                        label: "Business name",
                        value: "Limassol Airport Taxi",
                        icon: BriefcaseBusiness,
                      },
                      {
                        label: "Website",
                        value: "limassolairporttaxi.com",
                        icon: Globe2,
                      },
                      {
                        label: "Location",
                        value: "Limassol, Cyprus",
                        icon: MapPin,
                      },
                      {
                        label: "Contact phone",
                        value: "+357 96613191",
                        icon: Phone,
                      },
                      {
                        label: "Contact email",
                        value: "info@limassolairporttaxi.com",
                        icon: Mail,
                      },
                    ].map(({ label, value, icon: Icon }) => (
                      <SettingsCard key={label}>
                        <span className={`mb-4 inline-flex rounded-xl p-2 ${isDark ? "bg-white/10 text-yellow-300" : "bg-white text-yellow-700"}`}>
                          <Icon size={18} />
                        </span>
                        <p className={`text-sm font-bold ${ui.mutedText}`}>
                          {label}
                        </p>
                        <p className={`mt-2 break-words text-lg font-black ${ui.strongText}`}>
                          {value}
                        </p>
                      </SettingsCard>
                    ))}
                  </div>
                </section>
              )}

              {activeTab === "business" && (
                <section className={`rounded-2xl border p-5 sm:p-6 ${ui.card}`}>
                <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className={`text-xl font-black ${ui.strongText}`}>
                      Business information
                    </h2>
                    <p className={`mt-1 text-sm ${ui.mutedText}`}>
                      Core public-facing details for taxi bookings and customer
                      contact.
                    </p>
                  </div>
                  <span className={`w-fit rounded-full px-3 py-1 text-xs font-bold ${isDark ? "bg-blue-400/10 text-blue-200" : "bg-blue-50 text-blue-700"}`}>
                    Local profile
                  </span>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {businessFields.map(({ label, value, icon: Icon }) => (
                    <label key={label} className="block">
                      <span className={`mb-2 flex items-center gap-2 text-sm font-bold ${ui.bodyText}`}>
                        <Icon size={16} className={ui.faintText} />
                        {label}
                      </span>
                      <input
                        defaultValue={value}
                        className={`h-12 w-full rounded-2xl border px-4 text-sm font-semibold outline-none transition focus:border-yellow-400 focus:ring-4 focus:ring-yellow-100 ${ui.input}`}
                      />
                    </label>
                  ))}
                </div>

                <div className={`mt-6 flex flex-col gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between ${ui.divider}`}>
                  <p className={`text-sm ${ui.mutedText}`}>
                    Changes here are local UI settings until connected to a
                    persistent settings store.
                  </p>
                  <button className="rounded-2xl bg-yellow-400 px-6 py-3 text-sm font-black text-slate-950 transition hover:bg-yellow-300">
                    Save Settings
                  </button>
                </div>
              </section>
              )}

              {activeTab === "security" && (
                <section className={`rounded-2xl border p-5 sm:p-6 ${ui.card}`}>
                  <div className="mb-5 flex items-start justify-between gap-4">
                    <div>
                      <h2 className={`text-lg font-black ${ui.strongText}`}>
                        Security Settings
                      </h2>
                      <p className={`mt-1 text-sm ${ui.mutedText}`}>
                        Admin access, password reset, and protected data rules.
                      </p>
                    </div>
                    <span className={`rounded-2xl p-3 ${isDark ? "bg-emerald-400/10 text-emerald-300" : "bg-emerald-50 text-emerald-600"}`}>
                      <LockKeyhole size={20} />
                    </span>
                  </div>

                  <div className="space-y-3">
                    {securityItems.map(({ label, value, helper }) => (
                      <SettingsCard key={label}>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <p className={`text-sm font-bold ${ui.bodyText}`}>
                            {label}
                          </p>
                          <span className={`w-fit rounded-full px-3 py-1 text-xs font-black ${isDark ? "bg-white/10 text-white" : "bg-white text-slate-800"}`}>
                            {value}
                          </span>
                        </div>
                        <p className={`mt-2 text-sm ${ui.mutedText}`}>{helper}</p>
                      </SettingsCard>
                    ))}
                  </div>

                  <Link
                    to="/update-password"
                    className={`mt-5 inline-flex items-center justify-center rounded-2xl border px-5 py-3 text-sm font-black transition ${
                      isDark
                        ? "border-white/10 bg-white/5 text-white hover:bg-white/10"
                        : "border-slate-200 bg-white text-slate-800 hover:border-yellow-300 hover:bg-yellow-50"
                    }`}
                  >
                    Update password
                  </Link>
                </section>
              )}

              {activeTab === "notifications" && (
                <section className={`rounded-2xl border p-5 sm:p-6 ${ui.card}`}>
                  <div className="mb-5 flex items-start justify-between gap-4">
                    <div>
                      <h2 className={`text-lg font-black ${ui.strongText}`}>
                        Notifications
                      </h2>
                      <p className={`mt-1 text-sm ${ui.mutedText}`}>
                        Customer contact and performance tracking signals.
                      </p>
                    </div>
                    <span className={`rounded-2xl p-3 ${isDark ? "bg-blue-400/10 text-blue-200" : "bg-blue-50 text-blue-600"}`}>
                      <Bell size={20} />
                    </span>
                  </div>

                  <div className="space-y-3">
                    {notificationItems.map(({ label, value, helper, icon: Icon }) => (
                      <SettingsCard key={label} className="flex gap-3">
                        <span className={`mt-1 h-fit rounded-xl p-2 ${isDark ? "bg-white/10 text-neutral-400" : "bg-white text-slate-500"}`}>
                          <Icon size={17} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <p className={`text-sm font-bold ${ui.bodyText}`}>
                              {label}
                            </p>
                            <span className={`w-fit rounded-full px-3 py-1 text-xs font-black ${isDark ? "bg-emerald-400/10 text-emerald-300" : "bg-emerald-50 text-emerald-700"}`}>
                              {value}
                            </span>
                          </div>
                          <p className={`mt-2 text-sm ${ui.mutedText}`}>{helper}</p>
                        </div>
                      </SettingsCard>
                    ))}
                  </div>
                </section>
              )}

              {activeTab === "integrations" && (
                <section className={`rounded-2xl border p-5 sm:p-6 ${ui.card}`}>
                <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className={`text-xl font-black ${ui.strongText}`}>
                      Integrations
                    </h2>
                    <p className={`mt-1 text-sm ${ui.mutedText}`}>
                      Connected services used by the admin and public booking
                      flow.
                    </p>
                  </div>
                  <span className={`flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-black ${isDark ? "bg-white/10 text-neutral-300" : "bg-slate-100 text-slate-600"}`}>
                    <Plug size={14} />
                    Platform services
                  </span>
                </div>

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {integrations.map(({ name, status, detail }) => (
                    <SettingsCard key={name}>
                      <div className="mb-4 flex items-start justify-between gap-3">
                        <div>
                          <p className={`font-black ${ui.strongText}`}>{name}</p>
                          <p className={`mt-1 text-sm ${ui.mutedText}`}>{detail}</p>
                        </div>
                        <CheckCircle2
                          size={20}
                          className="shrink-0 text-emerald-500"
                        />
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-black ${isDark ? "bg-white/10 text-emerald-300" : "bg-white text-emerald-700"}`}>
                        {status}
                      </span>
                    </SettingsCard>
                  ))}
                </div>
              </section>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default SettingsPage;
